import React, { useState, useEffect } from 'react';
import { X, Download, AlertCircle, CheckCircle, Loader, Shield, Clock } from 'lucide-react';
import '../styles/UnsealProgressModal.css';
import config from '../config/config';

const UnsealProgressModal = ({
  isOpen,
  onClose,
  onComplete,
  noTiket,
  namaIgt,
  mode = 'unseal' // 'unseal' or 'seal'
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [message, setMessage] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [downloadData, setDownloadData] = useState(null);
  const [processStarted, setProcessStarted] = useState(false);
  const [showBackgroundProgress, setShowBackgroundProgress] = useState(false);

  const stages = mode === 'seal' ? [
    { name: 'Mengirim ke Antrian', minProgress: 0, maxProgress: 20 },
    { name: 'Diproses Server', minProgress: 20, maxProgress: 60 },
    { name: 'Enkripsi BSSN', minProgress: 60, maxProgress: 90 },
    { name: 'Selesai', minProgress: 90, maxProgress: 100 }
  ] : [
    { name: 'Inisialisasi', minProgress: 0, maxProgress: 10 },
    { name: 'Persiapan', minProgress: 5, maxProgress: 15 },
    { name: 'Download dari MinIO', minProgress: 10, maxProgress: 40 },
    { name: 'Dekripsi', minProgress: 45, maxProgress: 80 },
    { name: 'Download File', minProgress: 80, maxProgress: 95 },
    { name: 'Selesai', minProgress: 95, maxProgress: 100 }
  ];

  const getStageIcon = () => {
    if (hasError) return <AlertCircle className="text-red-500" size={20} />;
    if (isComplete) return <CheckCircle className="text-green-500" size={20} />;
    if (showBackgroundProgress) return <Clock className="text-orange-500" size={20} />;
    return mode === 'seal'
      ? <Shield className="text-blue-500 unseal-spinner" size={20} />
      : <Loader className="text-blue-500 unseal-spinner" size={20} />;
  };

  const getTitle = () => {
    if (hasError) return `Error ${mode === 'seal' ? 'Enkripsi' : 'Dekripsi'}`;
    if (isComplete) return `${mode === 'seal' ? 'Enkripsi' : 'Dekripsi'} Selesai`;
    if (showBackgroundProgress) return `${mode === 'seal' ? 'Enkripsi' : 'Dekripsi'} Berjalan di Background`;
    return `${mode === 'seal' ? 'Mengenkripsi' : 'Mendekripsi'} File`;
  };

  // Simulasi progress untuk mode seal (antrian)
  const simulateQueueProgress = () => {
    let currentProgress = 0;
    setCurrentStage('Mengirim ke Antrian');
    setMessage('Mengirim permintaan enkripsi ke sistem antrian...');

    const stage1Timer = setInterval(() => {
      if (hasError || isComplete || showBackgroundProgress) {
        clearInterval(stage1Timer);
        return;
      }

      currentProgress += 2;
      setProgress(Math.min(currentProgress, 20));

      if (currentProgress >= 20) {
        clearInterval(stage1Timer);
        setCurrentStage('Diproses Server');
        setMessage('Permintaan telah masuk antrian dan sedang diproses...');

        const stage2Timer = setInterval(() => {
          if (hasError || isComplete || showBackgroundProgress) {
            clearInterval(stage2Timer);
            return;
          }

          currentProgress += 1;
          setProgress(Math.min(currentProgress, 60));
          if (currentProgress >= 60) {
            clearInterval(stage2Timer);
            setCurrentStage('Enkripsi BSSN');
            setMessage('File sedang dienkripsi menggunakan sistem keamanan BSSN...');
            const stage3Timer = setInterval(() => {
              if (hasError || isComplete || showBackgroundProgress) {
                clearInterval(stage3Timer);
                return;
              }
              currentProgress += 0.5;
              setProgress(Math.min(currentProgress, 90));
              if (currentProgress >= 70 && !showBackgroundProgress) {
              }
            }, 200);
          }
        }, 150);
      }
    }, 100);
  };

  // Simulasi progress untuk unseal (seperti semula)
  const simulateProgress = () => {
    let currentProgress = 0;
    const progressStages = [
      { name: 'Inisialisasi', duration: 500, target: 5 },
      { name: 'Persiapan', duration: 1000, target: 15 },
      { name: 'Download dari MinIO', duration: 3000, target: 40 },
      { name: 'Dekripsi', duration: 5000, target: 80 },
      { name: 'Download File', duration: 2000, target: 95 }
    ];

    let stageIndex = 0;

    const updateProgress = () => {
      if (stageIndex >= progressStages.length || hasError || isComplete) return;

      const stage = progressStages[stageIndex];
      setCurrentStage(stage.name);

      const progressIncrement = (stage.target - currentProgress) / (stage.duration / 100);

      const progressTimer = setInterval(() => {
        if (hasError || isComplete) {
          clearInterval(progressTimer);
          return;
        }

        currentProgress += progressIncrement;
        setProgress(Math.min(currentProgress, stage.target));

        // Update message untuk unseal
        switch (stage.name) {
          case 'Inisialisasi':
            setMessage('Memulai proses dekripsi dan unduh...');
            break;
          case 'Persiapan':
            setMessage('File ditemukan, mempersiapkan download...');
            break;
          case 'Download dari MinIO':
            setMessage('Mendownload file terenkripsi dari storage...');
            break;
          case 'Dekripsi':
            setMessage('Mengirim file ke server dekripsi...');
            break;
          case 'Download File':
            setMessage('Dekripsi berhasil, mempersiapkan download...');
            break;
        }

        if (currentProgress >= stage.target) {
          clearInterval(progressTimer);
          stageIndex++;

          if (stageIndex < progressStages.length) {
            setTimeout(updateProgress, 200);
          } else {
            // Untuk unseal, tunggu download selesai
            setCurrentStage('Download File');
            setMessage('Memulai download file hasil dekripsi...');
          }
        }
      }, 100);
    };

    updateProgress();
  };

  const startProcess = async () => {
    try {
      setProcessStarted(true);
      const token = localStorage.getItem('token');

      if (mode === 'seal') {
        // Mulai simulasi progress antrian
        simulateQueueProgress();

        // Kirim request ke API
        const response = await fetch(
          `${config.API_URL}/dataAdmin/seal/${noTiket}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Jika berhasil dikirim ke antrian, tapi belum selesai diproses
        if (!showBackgroundProgress) {
          setProgress(90);
          setCurrentStage('Diproses Server');
          setMessage('Permintaan enkripsi telah berhasil dikirim ke antrian. Proses enkripsi akan berjalan di background.');
        }

      } else {
        // Untuk unseal, gunakan progress seperti semula
        simulateProgress();

        const response = await fetch(
          `${config.API_URL}/dataAdmin/unseal-download/${btoa(noTiket)}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Handle download untuk unseal
        const contentDisposition = response.headers.get('content-disposition');
        let filename = `Peta_${namaIgt || noTiket}.zip`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch && filenameMatch.length === 2) {
            filename = filenameMatch[1];
          }
        }

        const blob = await response.blob();

        setProgress(100);
        setCurrentStage('Selesai');
        setMessage('Download selesai!');
        setIsComplete(true);

        // Store download data
        setDownloadData({ blob, filename });
      }

    } catch (error) {
      console.error(`Gagal memulai proses ${mode}:`, error);
      setHasError(true);
      setErrorMessage(`Gagal ${mode === 'seal' ? 'mengenkripsi' : 'mendekripsi dan mengunduh'} file: ${error.message}`);
    }
  };

  const downloadFile = () => {
    if (!downloadData || mode === 'seal') return;

    const url = window.URL.createObjectURL(downloadData.blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', downloadData.filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    if (onComplete) {
      onComplete();
    }
  };

  const handleBackgroundProgress = () => {
    setShowBackgroundProgress(true);
    if (onComplete) {
      onComplete({ status: 'queued', message: 'Proses enkripsi berjalan di background' });
    }
  };

  const handleClose = () => {
    if (!processStarted || isComplete || hasError || showBackgroundProgress) {
      onClose();
    }
  };

  // Start process when modal opens
  useEffect(() => {
    if (isOpen && !hasError && !isComplete && !processStarted) {
      const timer = setTimeout(() => {
        startProcess();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, hasError, isComplete, processStarted, mode]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProgress(0);
      setCurrentStage('');
      setMessage('');
      setIsComplete(false);
      setHasError(false);
      setErrorMessage('');
      setDownloadData(null);
      setProcessStarted(false);
      setShowBackgroundProgress(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="unseal-modal-overlay">
      <div className="unseal-modal">
        {/* Header */}
        <div className="unseal-modal-header">
          <div className="unseal-modal-title-section">
            {getStageIcon()}
            <h3 className="unseal-modal-title">{getTitle()}</h3>
          </div>
          {(hasError || isComplete || showBackgroundProgress) && (
            <button
              onClick={handleClose}
              className="unseal-modal-close-btn"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="unseal-modal-content">
          {hasError ? (
            <div>
              <div className="unseal-error-section">
                <div className="unseal-error-icon">
                  <AlertCircle className="text-red-500" size={24} />
                </div>
                <p className="unseal-error-text">{errorMessage}</p>
              </div>
              <div className="unseal-action-buttons">
                <button
                  onClick={handleClose}
                  className="unseal-btn unseal-btn-primary"
                >
                  Tutup
                </button>
              </div>
            </div>
          ) : showBackgroundProgress ? (
            <div>
              <div className="unseal-success-section">
                <div className="unseal-success-icon">
                  <Clock className="text-orange-500" size={24} />
                </div>
                <p className="unseal-success-text">
                  Proses enkripsi sedang berjalan di background menggunakan sistem antrian.
                </p>
                <div className="mt-3 p-3 bg-light rounded">
                  <small className="text-muted">
                    <Shield size={16} className="me-1" />
                    Proses enkripsi akan memakan waktu beberapa menit tergantung antrian server.
                    File akan otomatis terenkripsi ketika proses selesai.
                  </small>
                </div>
              </div>
              <div className="unseal-action-buttons">
                <button
                  onClick={handleClose}
                  className="unseal-btn unseal-btn-primary"
                >
                  OK
                </button>
              </div>
            </div>
          ) : isComplete ? (
            <div>
              <div className="unseal-success-section">
                <div className="unseal-success-icon">
                  <CheckCircle className="text-green-500" size={24} />
                </div>
                <p className="unseal-success-text">
                  {mode === 'seal'
                    ? 'File berhasil dienkripsi dan disimpan dengan aman!'
                    : 'File berhasil didekripsi dan siap untuk diunduh!'
                  }
                </p>

                {mode === 'seal' && (
                  <div className="mt-3 p-3 bg-light rounded">
                    <small className="text-muted">
                      <Shield size={16} className="me-1" />
                      File asli telah dihapus dan diganti dengan versi terenkripsi menggunakan sistem keamanan BSSN.
                    </small>
                  </div>
                )}

                {mode === 'unseal' && (
                  <button
                    onClick={downloadFile}
                    className="unseal-download-btn"
                  >
                    <Download size={16} />
                    Unduh File
                  </button>
                )}
              </div>
              <div className="unseal-action-buttons">
                <button
                  onClick={handleClose}
                  className="unseal-btn unseal-btn-primary"
                >
                  {mode === 'seal' ? 'Selesai' : 'Tutup'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              {/* Progress Bar */}
              <div className="unseal-progress-section">
                <div className="unseal-progress-header">
                  <span className="unseal-progress-stage">
                    {currentStage || 'Memulai...'}
                  </span>
                  <span className="unseal-progress-percentage">
                    {Math.round(progress)}%
                  </span>
                </div>
                <div className="unseal-progress-bar-container">
                  <div
                    className="unseal-progress-bar"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>

              {/* Progress Message */}
              <div className="unseal-message-section">
                <p className="unseal-message-text">
                  {message || `Mempersiapkan proses ${mode === 'seal' ? 'enkripsi' : 'dekripsi'}...`}
                </p>
              </div>

              {/* Stage Indicators */}
              <div className="unseal-stages-container">
                {stages.map((stage, index) => {
                  const isCurrentStage = currentStage === stage.name;
                  const isCompleted = progress > stage.maxProgress;
                  const isInProgress = progress >= stage.minProgress && progress <= stage.maxProgress;

                  let stageClass = 'pending';
                  if (isCompleted) stageClass = 'completed';
                  else if (isCurrentStage || isInProgress) stageClass = 'current';

                  return (
                    <div
                      key={stage.name}
                      className={`unseal-stage-item ${stageClass}`}
                    >
                      <div className={`unseal-stage-indicator ${stageClass}`}></div>
                      <span>{stage.name}</span>
                      {isCurrentStage && (
                        <Loader className="unseal-spinner" size={12} />
                      )}
                      {isCompleted && (
                        <CheckCircle size={12} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons untuk Seal Mode */}
              {mode === 'seal' && progress >= 70 && progress < 100 && (
                <div className="unseal-action-buttons mt-3">
                  <button
                    onClick={handleBackgroundProgress}
                    className="unseal-btn unseal-btn-secondary"
                  >
                    <Clock size={16} className="me-2" />
                    Biarkan Berjalan di Background
                  </button>
                </div>
              )}

              <div className="unseal-note-section">
                <p className="unseal-note-text">
                  {mode === 'seal' ? (
                    <>
                      <Shield size={14} className="me-1" />
                      Proses enkripsi menggunakan sistem antrian dan keamanan BSSN untuk melindungi data Anda.
                      Anda dapat membiarkan proses berjalan di background.
                    </>
                  ) : (
                    'Proses ini mungkin memakan waktu beberapa menit tergantung ukuran file'
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsealProgressModal;